import { Html } from "@react-three/drei";
import { useId, useState } from "react";
import { partsOf, scoreFsp } from "../engine/fsp";
import type { FspParts, SignalObject } from "../engine/schema";
import type { SpatialNode } from "../engine/types";
import { useEngineStore } from "../store/engineStore";
import "./signal-label.css";

// Existing graph nodes remain unchanged. Optional inputs are read, never fabricated.
export type SignalLabelNode = SpatialNode & {
  signal?: SignalObject;
  fspParts?: Partial<FspParts>;
  /** Existing canonical FSP scores are normalized to 0–1. */
  fsp?: number | null;
};

const FSP_FACTORS: { key: keyof FspParts; label: string }[] = [
  { key: "velocity", label: "Velocity" },
  { key: "acceleration", label: "Acceleration" },
  { key: "convergence", label: "Cross-genre convergence" },
  { key: "djAdoption", label: "DJ adoption" },
  { key: "diffusion", label: "Geographic diffusion" },
  { key: "socialReuse", label: "Social reuse" },
  { key: "technology", label: "Technology enablement" },
  { key: "novelty", label: "Novelty" },
];

function validFactor(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= 1;
}

function compactTitle(title: string) {
  const text = title.replace(/\s+/g, " ").trim();
  return text.length <= 52 ? text : `${text.slice(0, 51).replace(/\s+\S*$/, "")}…`;
}

/** Render beside the planet, outside its rotating/scaling visual body. */
export function SignalNodeLabel({ node }: { node: SignalLabelNode }) {
  const select = useEngineStore((state) => state.select);
  const tooltipId = useId();
  const [dismissed, setDismissed] = useState(false);
  const factors = node.signal ? partsOf(node.signal) : node.fspParts;
  const score = factors
    ? FSP_FACTORS.every(({ key }) => validFactor(factors[key])) ? scoreFsp(factors as FspParts) : null
    : validFactor(node.fsp) ? node.fsp : null;
  const scoreLabel = validFactor(score) ? `Estimated FSP ${Math.round(score * 100)}` : "Estimated FSP · Unavailable";
  const status = !validFactor(score) ? "unavailable" : score >= 0.75 ? "strong" : score < 0.5 ? "weak" : "forming";
  const hypothesis = node.signal?.plainWhy || node.why;

  return (
    <Html
      center
      distanceFactor={15}
      occlude
      position={[0, -node.look.radius * 1.65 - 0.4, 0]}
      zIndexRange={[30, 0]}
      style={{ width: 220, maxWidth: "220px", textAlign: "center", pointerEvents: "auto" }}
    >
      <div
        className="signal-label"
        data-status={status}
        data-dismissed={dismissed}
        onPointerDown={(event) => event.stopPropagation()}
        onPointerUp={(event) => event.stopPropagation()}
        onPointerMove={(event) => event.stopPropagation()}
        onWheel={(event) => event.stopPropagation()}
        onPointerLeave={() => setDismissed(false)}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.stopPropagation();
            setDismissed(true);
          }
        }}
      >
        <button
          className="signal-label__summary"
          type="button"
          aria-label={`${node.name}. ${scoreLabel}. Inspect evidence.`}
          aria-describedby={tooltipId}
          onFocus={() => setDismissed(false)}
          onClick={(event) => { event.stopPropagation(); select(node.id); }}
        >
          <strong>{compactTitle(node.name)}</strong>
          <span>{scoreLabel}</span>
        </button>
        <div className="signal-label__bridge">
          <section id={tooltipId} className="signal-label__evidence" role="tooltip">
            <p className="signal-label__kind">Projected hypothesis</p>
            <p className="signal-label__hypothesis">{node.name}</p>
            {hypothesis && hypothesis !== node.name && <p>{hypothesis}</p>}
            <dl className="signal-label__factors">
              {FSP_FACTORS.map(({ key, label }) => {
                const value = factors?.[key];
                return (
                  <div key={key}>
                    <dt>{label}</dt>
                    <dd>{validFactor(value) ? `${Math.round(value * 100)}/100` : "Unavailable"}</dd>
                  </div>
                );
              })}
            </dl>
            <p className="signal-label__note">
              Model estimate, not live telemetry. Factor values are normalized scores, not measured growth percentages.
              {!validFactor(score) && " No complete factor set or existing score was supplied."}
            </p>
          </section>
        </div>
      </div>
    </Html>
  );
}
